import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { debounceTime } from 'rxjs/operators';
import { GetDynFormBloc, SubmitDynFormBloc } from './dyn-form.blocs';


@Component({
    selector: 'app-dyn-form',
    templateUrl: './dyn-form.component.html',
    styleUrls: ['./dyn-form.component.scss'],
    providers: [
        GetDynFormBloc,
        SubmitDynFormBloc
    ],
    changeDetection: ChangeDetectionStrategy.Default
})
export class DynFormComponent implements OnInit, OnDestroy {
    @ViewChild('pdf') pdf;

    @Output() isSubmitted = new EventEmitter<boolean>();
    @Output() valueChanged = new EventEmitter<boolean>();
    @Output() valid = new EventEmitter<boolean>();
    @Output() rendered = new EventEmitter<boolean>();
    @Output() result = new EventEmitter<any>();
    @Output() triggerCustomSubmission: EventEmitter<boolean> = new EventEmitter(false);

    @Input() showTitle = true;
    @Input() updatedOnKey: string;
    @Input() title: string;
    @Input() horizontalMode: boolean;
    @Input() public autoSave: boolean;
    @Input() public message: string;
    @Input() hasCustomSubmission: boolean = false;

    @Input()
    set key(value: string) {
        this.getDynForm.setKey(value);
    }
    @Input() public set options(value: FormlyFormOptions) {
        this._options = value;
    }
    @Input() public set model(value) {
        this._model = value;
        this._originalData = JSON.parse(JSON.stringify(value));

        if (!!this._options.formState) {
            this._options.formState.model = this._model;
        }
        this.form.markAsPristine();
    }
    @Input() set isEditMode(value: boolean) {
        this._editMode = value;
        if (this.formResult) {
            this.fields = this.formResult.configuration.fields;
            this.model.isEditMode = this.isEditMode;
            if (!!this._options.formState) {
                this._options.formState.model = this._model;
            }
            this._setFieldsMode(this.formResult.configuration.fields, !this.isEditMode);
            this._cdRef.markForCheck();
        }
    }

    public get options() {
        return this._options;
    }

    public get model() {
        return this._model;
    }


    public form = new FormGroup({});
    public fields: FormlyFieldConfig[] = [];
    public printMode = false;
    public formResult;

    private _isValid: boolean = false;
    private  _oldStatus = 'INVALID';
    private _isBusy: boolean = false;
    private _originalData;
    private _options: FormlyFormOptions = { formState: { model: {} } };
    private _editMode = false;
    private _model;


    get isEditMode(): boolean {
        return this._editMode;
    }

    constructor(
        private _cdRef: ChangeDetectorRef,
        private matSnackBar: MatSnackBar,
        public getDynForm: GetDynFormBloc,
        public submitDynForm: SubmitDynFormBloc
    ) {
        this.getDynForm.result.subscribe(res => {
            this.formResult = res.output;
            this.fields = this.formResult.configuration.fields;
            this.rendered.emit(true);
            this._setFieldsMode(this.formResult.configuration.fields, !this.isEditMode);
            _cdRef.markForCheck();
        });
    }

    public ngOnInit():void {
        this.submitDynForm.result.subscribe(result => {
            if (result.output.result) {
                this.result.emit(result.output.result);
                this.matSnackBar.open(this.message ? this.message : 'Saved Successfully.', '', {
                    duration: 3000,
                    panelClass: ['success-snackbar']
                });
            }
            if (result.output.error) {
                this.result.emit(false);
                this.matSnackBar.open(result.output.error.Message || result.output.error, '', {
                    duration: 5000,
                    panelClass: ['error-snackbar']
                });
            }
            this._isBusy = result.output.isBusy;
            if (!this._cdRef['destroyed']) {
                this._cdRef.markForCheck();
            }
        });
        this._subscribeToStatusChange();
        this._subscribeToValueChange();
    }

    public cancel(): void {
        this.model = this._originalData;
    }

    public async submit(): Promise<void> {
        this.hasCustomSubmission === false ? await this.submitForm() : this.triggerCustomSubmission.emit(true)
    }

    public async submitForm(): Promise<void> {
        if (this.form.valid && !this._isBusy) {
            if (this.updatedOnKey) {
                this.model[this.updatedOnKey] = new Date();
            }
            await this.submitDynForm.submit(this.model);
            this.isSubmitted.emit(true);
        } else if (!this.form.valid) {
            const invalidFields = this.findInvalidControlsRecursive(this.form).map(x => this.getField(x, this.fields).templateOptions.label).join(', ');
            this.matSnackBar.open(`Field(s) ${invalidFields} are not valid`, 'X', {
                duration: 30000,
                horizontalPosition: 'center',
                verticalPosition: 'top',
                panelClass: ['error-snackbar'],
            });
            this._cdRef.markForCheck();
        }
    }

    public print() :void {
        this.printMode = true;
        this._cdRef.markForCheck();
        this.pdf.saveAs(this.title);
        this.printMode = false;
        this._cdRef.markForCheck();
    }

    private _subscribeToValueChange():void {
        this.form.valueChanges.subscribe((value: any) => {
            if (this.form.dirty) {
                this.valueChanged.emit(value);
                if (this.autoSave) {
                    this.submit();
                }
            }
        });
    }

    private _subscribeToStatusChange() {
        this.form.statusChanges.pipe(
            debounceTime(100)
        ).subscribe(status => {
            this._isValid = false;
            this._isValid = this.form.status === 'VALID';
            this.valid.emit(this._isValid);
            if (!this._cdRef['destroyed']) {
                this._cdRef.markForCheck();
            }
            if (this._oldStatus !== 'DISABLED' && status !== 'DISABLED' && this._oldStatus !== status) {
                this.form.markAsDirty();
                this.form.markAllAsTouched();
                this._oldStatus = status;
            }
        });
    }

    private _setFieldsMode(fields: FormlyFieldConfig[], isDisabled: boolean) {
        fields.forEach((field) => {
            field = this._setFieldMode(field, isDisabled);
            if (field.fieldArray) {
                this._setFieldMode(field.fieldArray, isDisabled);
            }
        });
    }

    public findInvalidControlsRecursive(formToInvestigate: FormGroup | FormArray): string[] {
        const invalidControls: string[] = [];
        const recursiveFunc = (form: FormGroup | FormArray) => {
            Object.keys(form.controls).forEach(field => {
                const control = form.get(field);
                if (control.invalid && !(control instanceof FormArray) && !(control instanceof FormGroup)) {
                    invalidControls.push(field);
                    control.markAsDirty();
                    control.markAsTouched();
                }
                if (control instanceof FormGroup || control instanceof FormArray) {
                    recursiveFunc(control);
                }
            });
        }
        recursiveFunc(formToInvestigate);
        return invalidControls;
    }

    private _setFieldMode(
        field: FormlyFieldConfig,
        isDisabled: boolean
    ): FormlyFieldConfig {
        let to = field?.templateOptions || {};
        to.disabled = to.readOnly || isDisabled;
        if (field.fieldGroup) {
            this._setFieldsMode(field.fieldGroup, isDisabled);
        }
        return field;
    }

    private getField(key: string, fields: FormlyFieldConfig[]): FormlyFieldConfig {
        for (let i = 0, len = fields.length; i < len; i++) {
            const f = fields[i];
            if (f.key === key) {
                return f;
            }
            if ((f.fieldGroup || f.fieldArray)) {
                const cf = this.getField(key, f.fieldGroup);
                if (cf) {
                    return cf;
                }
            }
        }
    }

    ngOnDestroy(): void {
        this.getDynForm.dispose();
        this.submitDynForm.dispose();
        this._cdRef.detach();
    }
}

